import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { Observable, map, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

/** Firma przypisana zalogowanemu kadrowcowi (z hr-gateway). */
export interface AssignedCompany {
  id: string;
  name: string;
  nip?: string;
}

const SELECTED_COMPANY_KEY = 'selected_hr_company_id';

interface AssignedCompanyApiItem {
  companyId: string;
  name: string;
  nip?: string;
}

/**
 * Zarządza listą firm przypisanych kadrowcowi oraz aktualnie wybraną firmą.
 * Wybrana firma trafia do nagłówka `company-id` (auth.interceptor) i bramkuje
 * dostęp po stronie hr-gateway (HrTenantMiddleware).
 */
@Injectable({ providedIn: 'root' })
export class HrCompaniesService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly _companies = signal<AssignedCompany[]>([]);
  private readonly _selectedCompanyId = signal<string | null>(this.loadSelectedCompanyId());
  private readonly _loaded = signal(false);

  readonly companies = this._companies.asReadonly();
  readonly selectedCompanyId = this._selectedCompanyId.asReadonly();
  readonly loaded = this._loaded.asReadonly();

  readonly selectedCompany = computed(() => {
    const id = this._selectedCompanyId();
    return this._companies().find((c) => c.id === id) ?? null;
  });

  readonly hasMultipleCompanies = computed(() => this._companies().length > 1);

  /** Pobiera firmy przypisane kadrowcowi; auto-wybiera, gdy jest dokładnie jedna. */
  loadCompanies(): Observable<AssignedCompany[]> {
    return this.http
      .get<{ data: AssignedCompanyApiItem[] }>(`${environment.apiUrl}/hr/companies`)
      .pipe(
        map((res) => (res.data ?? []).map(mapCompany)),
        tap((companies) => {
          this._companies.set(companies);
          this._loaded.set(true);
          this.reconcileSelection(companies);
        }),
      );
  }

  selectCompany(id: string): void {
    this._selectedCompanyId.set(id);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(SELECTED_COMPANY_KEY, id);
    }
  }

  clear(): void {
    this._companies.set([]);
    this._selectedCompanyId.set(null);
    this._loaded.set(false);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(SELECTED_COMPANY_KEY);
    }
  }

  /** Po pobraniu listy: auto-wybór gdy jedna firma; czyszczenie gdy wybrana zniknęła. */
  private reconcileSelection(companies: AssignedCompany[]): void {
    const current = this._selectedCompanyId();
    const stillValid = current !== null && companies.some((c) => c.id === current);

    if (stillValid) return;

    if (companies.length === 1) {
      this.selectCompany(companies[0].id);
    } else if (current !== null) {
      // Wybrana wcześniej firma nie jest już przypisana — wyczyść wybór.
      this._selectedCompanyId.set(null);
      if (isPlatformBrowser(this.platformId)) {
        localStorage.removeItem(SELECTED_COMPANY_KEY);
      }
    }
  }

  private loadSelectedCompanyId(): string | null {
    try {
      if (typeof localStorage === 'undefined') return null;
      return localStorage.getItem(SELECTED_COMPANY_KEY);
    } catch {
      return null;
    }
  }
}

function mapCompany(item: AssignedCompanyApiItem): AssignedCompany {
  return {
    id: item.companyId,
    name: item.name?.trim() || item.companyId,
    nip: item.nip || undefined,
  };
}
